import { useApolloClient, useQuery } from "@apollo/client";
import {
  faAngleLeft,
  faAngleRight,
  faClose,
  faFileCircleMinus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { SubmitButton } from "Components/Button";
import { LcpUserPageWrapper } from "Components/MainPageWrapper";
import { Section } from "Components/Section";
import { tableClassNames, TableHeading } from "Components/TableHelpers";
import {
  ApplicationStatus,
  archiveApplicationMutation,
  SearchApplicationsQuery,
  searchApplicationsQuery,
} from "generated/graphql";
import { EnterKeyCatcher } from "Inputs/EnterKeyCatcher";
import { createFieldProps } from "Inputs/FormTools";
import { Textbox } from "Inputs/Textbox";
import React, { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { formatDollarsAndCents, hasValue, hasValueFn, range } from "Tools";
import { ApplicationSearchResult } from "./AdminDashboard/AdminDashboard";
import { getCaseStatus, getClaimStatus } from "./ApplicationDetails/Tools";
import { DashboardLink } from "./DashboardLink";
import { AdminPaths } from "./Paths";

const resultsPerPage = 10;

export function SearchResultsPage() {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialSearchText = (queryParams.get("searchText") ?? "").trim();
  const rawPage = Number(queryParams.get("page"));
  const pageOneBased = rawPage > 1 ? rawPage : 1;

  const [rawSearchText, setRawSearchText] = useState<string | null>(
    initialSearchText,
  );
  const field = createFieldProps({
    name: "searchText",
    value: rawSearchText,
    setValue: setRawSearchText,
  });
  const { data, refetch } = useQuery(searchApplicationsQuery, {
    variables: {
      input: {
        searchText: initialSearchText,
        page: pageOneBased - 1,
        resultsPerPage,
      },
    },
  });
  const onSearch = async () => {
    history.push(AdminPaths.SearchResults({ searchText: rawSearchText ?? "" }));
  };
  return (
    <LcpUserPageWrapper
      documentTitle="Search Policies and Applications"
      theme={{ mainDivStyle: { maxWidth: "920px" } }}
    >
      <Section
        title="Search Policies and Applications"
        subtitle="Search Applicant, Company or Case Name; Email, Phone, Case or Policy Number."
      >
        <EnterKeyCatcher handleSubmit={onSearch}>
          <Textbox
            fieldProps={field}
            leftIconContent={<FontAwesomeIcon icon={faSearch} />}
            placeholder="Search policies and/or applications"
            style={{ flexGrow: 1 }}
            rightIconContent={
              <FontAwesomeIcon
                icon={faClose}
                onClick={() => {
                  setRawSearchText(null);
                }}
                style={{ pointerEvents: "initial" }}
                className={classNames("click-catcher-small", {
                  "is-hidden": !hasValue(field.value),
                })}
              />
            }
          />
        </EnterKeyCatcher>
        <div className="buttons is-centered" style={{ marginTop: "1em" }}>
          <SubmitButton onClick={onSearch}>Search</SubmitButton>
        </div>
        {!data ? (
          <div className="has-text-centered">Loading...</div>
        ) : data.searchApplications.totalCount === 0 ? (
          <div className="has-text-centered">
            <em>No results found.</em>
          </div>
        ) : (
          <ResultsTable
            applications={data.searchApplications.applications}
            totalCount={data.searchApplications.totalCount}
            searchText={initialSearchText}
            page={pageOneBased}
            refetch={async () => {
              await refetch();
            }}
          />
        )}
        <DashboardLink />
      </Section>
    </LcpUserPageWrapper>
  );
}

function ArchiveAppButton(props: {
  app: ApplicationSearchResult;
  refetch: () => Promise<void>;
}) {
  const apolloClient = useApolloClient();
  return (
    <span
      className="click-catcher-small"
      onClickCapture={async e => {
        e.stopPropagation();
        if (
          window.confirm(
            `Archive application for ${props.app.applicant.fullName} - ${props.app.applicant.companyName}?`,
          )
        ) {
          await apolloClient.mutate({
            mutation: archiveApplicationMutation,
            variables: { input: { id: props.app.id } },
          });
          void props.refetch();
        }
      }}
    >
      <FontAwesomeIcon icon={faFileCircleMinus} />
    </span>
  );
}

function ResultsTable(props: {
  applications: SearchApplicationsQuery["searchApplications"]["applications"];
  totalCount: SearchApplicationsQuery["searchApplications"]["totalCount"];
  searchText: string;
  page: number;
  refetch: () => Promise<void>;
}) {
  const history = useHistory();

  return (
    <div className="has-text-centered">
      <table className={tableClassNames}>
        <tbody>
          <tr>
            <TableHeading>&nbsp;</TableHeading>
            <TableHeading>Policy Number</TableHeading>
            <TableHeading>Insured Name</TableHeading>
            <TableHeading>Policy Limit</TableHeading>
            <TableHeading>Case Name</TableHeading>
            <TableHeading>Case Status</TableHeading>
            <TableHeading>Claim Status</TableHeading>
          </tr>
          {props.applications.map(app => (
            <tr
              key={app.id}
              onClick={() => {
                history.push(AdminPaths.ApplicationDetails({ app }));
              }}
              style={{ cursor: "pointer" }}
            >
              <td style={{ whiteSpace: "nowrap" }}>
                {!app.purchaseRecord && (
                  <ArchiveAppButton app={app} refetch={props.refetch} />
                )}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {app.purchaseRecord?.policyNumber ?? "Application"}
                {app.status === ApplicationStatus.CANCELED
                  ? [
                      <br />,
                      <span style={{ fontStyle: "italic" }}>(Canceled)</span>,
                    ]
                  : null}
              </td>
              <td>
                {app.applicant.companyName} - {app.applicant.fullName}
              </td>
              <td>
                {hasValueFn(app.case?.policyLimit, formatDollarsAndCents)}
              </td>
              <td>{app.case?.caseName}</td>
              <td>{hasValueFn(app.case, getCaseStatus)}</td>
              <td>{hasValueFn(app.claim, getClaimStatus)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        page={props.page}
        pageCount={Math.ceil(props.totalCount / resultsPerPage)}
        getPageLink={(page: number) =>
          AdminPaths.SearchResults({
            searchText: props.searchText,
            page,
          })
        }
      />
    </div>
  );
}

function Pagination(props: {
  page: number;
  pageCount: number;
  getPageLink: (page: number) => string;
}) {
  const firstPage = 1;
  const finalPage = props.pageCount;
  const pageRangeMin = Math.max(props.page - 1, firstPage);
  const pageRangeMax = Math.min(props.page + 1, finalPage);
  return (
    <nav
      className="pagination is-centered is-small"
      role="navigation"
      aria-label="pagination"
    >
      <ul className="pagination-list">
        {props.page > firstPage && (
          <li>
            <Link
              className="pagination-previous"
              to={props.getPageLink(props.page - 1)}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </Link>
          </li>
        )}
        {pageRangeMin > firstPage && (
          <li>
            <Link className="pagination-link" to={props.getPageLink(firstPage)}>
              {firstPage}
            </Link>
          </li>
        )}
        {pageRangeMin > firstPage + 1 && (
          <li>
            <span className="pagination-ellipsis">&hellip;</span>
          </li>
        )}
        {range(pageRangeMin, pageRangeMax + 1).map(page => (
          <li key={page}>
            <Link
              className={classNames("pagination-link", {
                "is-current": page === props.page,
              })}
              aria-current={page === props.page ? "page" : undefined}
              to={props.getPageLink(page)}
            >
              {page}
            </Link>
          </li>
        ))}
        {pageRangeMax < finalPage - 1 && (
          <li>
            <span className="pagination-ellipsis">&hellip;</span>
          </li>
        )}
        {pageRangeMax < finalPage && (
          <li>
            <Link className="pagination-link" to={props.getPageLink(finalPage)}>
              {finalPage}
            </Link>
          </li>
        )}
        {props.page < finalPage && (
          <li>
            <Link
              className="pagination-next"
              to={props.getPageLink(props.page + 1)}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}
